'use client'
import { init, initAlt } from '@/app/api/init'
import { INIT_DATA_STORAGE_KEY } from '@/app/constants'
import { Currency, SanityData } from '@/app/types'
import { getCurrencyByCountry } from '@/app/utils'
import { setUser } from '@sentry/nextjs'
import { useLocalStorage } from '@uidotdev/usehooks'
import { Currencies } from 'currencies-map'
import dynamic from 'next/dynamic'
import {
	useParams,
	usePathname,
	useRouter,
	useSearchParams,
} from 'next/navigation'
import { FC, useEffect } from 'react'

interface InitCallerProps {
	sanityData?: SanityData
	setUserId?: (userId: string) => void
	isHomePage: boolean
}

interface InitItemData {
	userCountry?: string
	defaultCurrency?: Currency
	userIP?: string
	sessionID?: string
	initTimestamp?: string
	uid?: string
	userAgent: string
}

const joinItems = (item?: string | string[]) => {
	if (!item?.length) ''
	if (typeof item === 'string') return item
	return item?.join('|')
}

const InitCaller: FC<InitCallerProps> = (props) => {
	const { setUserId, isHomePage } = props
	const [initItem, setItem] = useLocalStorage<InitItemData>(
		INIT_DATA_STORAGE_KEY
	)
	const params = useParams()
	const searchParams = useSearchParams()
	const pathname = usePathname()
	const router = useRouter()
	const userParam = searchParams.get('user')
	const imidParam = searchParams.get('imid')
	const redirectParams = userParam && imidParam

	useEffect(() => {
		const queryParams = Object.fromEntries(Array.from(searchParams.entries()))

		const currentTime = new Date()
		const initTimestamp = initItem?.initTimestamp
			? new Date(initItem.initTimestamp)
			: null
		const isSessionValid =
			initItem?.sessionID &&
			initTimestamp &&
			currentTime.getTime() - initTimestamp.getTime() < 29 * 60 * 1000

		const performInit = async () => {
			try {
				let data
				if (redirectParams) {
					data = await initAlt({
						imid: imidParam,
						user: userParam,
						lng: params.lang as string,
					})
				} else {
					data = await init({
						...queryParams,
						lng: params.lang as string,
					})
				}

				if (!data) {
					console.error('init data is undefined')
					return
				}

				const userCurrency = getCurrencyByCountry(data?.userCountry as string)
				const { uid, imid, userCountry, ip, userAgent } = data

				const defaultCurrency: Currency = {
					label: Currencies.names.get(userCurrency) as string,
					flag: userCountry,
					abbreviation: userCurrency,
				}

				setUserId && setUserId(uid)

				const timestamp = new Date().toISOString()

				setItem({
					...(initItem || {}),
					userCountry,
					defaultCurrency,
					userIP: ip,
					sessionID: uid + imid,
					uid: uid,
					initTimestamp: timestamp,
					userAgent,
				})

				setUser({
					id: uid,
					username: imid,
				})

				if (typeof window.gtag !== 'undefined') {
					const userId = `${uid}${imid}`
					window.gtag('set', { user_id: userId })
				}
			} catch (error) {
				console.error('Error during initialization:', error)
			}
		}

		// if (
		// 	isHomePage ||
		// 	!isSessionValid ||
		// 	!initItem?.sessionID ||
		// 	redirectParams
		// ) {
		// } else {
		// 	setUserId(initItem.sessionID.slice(0, 32))
		// }
		performInit()
	}, [isHomePage, params.lang, searchParams])

	useEffect(() => {
		if (props.sanityData) {
			const current = new URLSearchParams(Array.from(searchParams.entries()))
			const { themes, stars, properties, facilities, currency } =
				props.sanityData

			const paramsMapping = {
				t: themes,
				s: stars,
				fa: facilities,
				p: properties,
				...(currency !== '0' && { currency }),
			}

			Object.entries(paramsMapping).forEach(([key, value]) => {
				if (value?.length) {
					current.set(key, joinItems(value) as string)
				}
			})
			const search = current.toString()
			router.push(`${pathname}?${search}`, { scroll: false })
		}
	}, [props.sanityData])

	return null
}

export default dynamic(() => Promise.resolve(InitCaller), { ssr: false })
